/* Estimate Hub — core controller (tabs, mini-bar, reveals, summary relay).
 *
 * Loaded (deferred) by every estimate-* section; self-guarded so it runs once.
 * Contract shared with estimate-hub-builder.js:
 *   window.bbEstimate = { get tab(), setTab(key, {source, scroll}), labels, enabled }
 *   document events: 'bb:est:ready' (once, {tab}) · 'bb:est:tab' ({tab, label})
 *   listens:         'bb:est:summary' ({tab, count, low, high, rangeText})
 * Markup hooks:
 *   [data-est-tabs="a b"]            shown only on listed tabs (gets data-est-on)
 *   [data-est-tab-button="key"]      toggle button, data-est-label, aria-pressed
 *   [data-est-tabgroup]              roving tabindex + arrow keys inside
 *   [data-est-text="tab_label"]      receives the current label, lowercased
 *   [data-est-summary-range/-cta]    written from bb:est:summary
 *   [data-est-minibar]               .is-on once the hero tab group scrolls away
 *                                    (off again under html.est-dock-on ≤999px)
 *   [data-est-dock]                  off state gets inline visibility:hidden
 *                                    (paint hygiene; builder owns the toggle)
 *   .est-rev                         scroll reveal (only when html.est-anim)
 * setTab's `scroll` option: true → the hero tab group; a selector string or an
 * Element → that target. Scrolling respects prefers-reduced-motion.
 */
document.documentElement.classList.add('est-js');
(function () {
  'use strict';
  if (window.__bbEstimateHub) return;
  window.__bbEstimateHub = true;

  var KEYS = ['weddings', 'personal', 'corporate', 'activations'];
  var FALLBACK_LABELS = { weddings: 'Weddings', personal: 'Personal events', corporate: 'Corporate events', activations: 'Brand activations' };
  var STORE_KEY = 'bb:est:tab';
  var html = document.documentElement;
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var state = { tab: null, labels: {}, enabled: [], ready: false, summary: null };

  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function reduced() { return reduceMQ.matches; }
  function tokens(el) {
    var raw = (el.getAttribute('data-est-tabs') || '').trim();
    return raw ? raw.split(/\s+/) : [];
  }
  function store(get, value) {
    try { return get ? window.sessionStorage.getItem(STORE_KEY) : window.sessionStorage.setItem(STORE_KEY, value); }
    catch (e) { return null; }
  }
  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  /* ---- labels + enabled keys (read from the buttons; hero first) ---------- */
  function collect() {
    var labels = {};
    var seen = {};
    $all('[data-est-tab-button]').forEach(function (btn) {
      var key = btn.getAttribute('data-est-tab-button');
      if (KEYS.indexOf(key) === -1) return;
      seen[key] = true;
      if (!labels[key]) labels[key] = (btn.getAttribute('data-est-label') || btn.textContent || '').trim();
    });
    state.enabled = KEYS.filter(function (k) { return seen[k]; });
    state.labels = {};
    KEYS.forEach(function (k) { state.labels[k] = labels[k] || FALLBACK_LABELS[k]; });
  }
  function isUsable(key) {
    if (KEYS.indexOf(key) === -1) return false;
    return state.enabled.length ? state.enabled.indexOf(key) > -1 : true;
  }
  function resolveInitial() {
    var candidates = [];
    try { candidates.push(new URLSearchParams(window.location.search).get('tab')); } catch (e) { /* no URLSearchParams */ }
    candidates.push((window.location.hash || '').replace(/^#/, ''));
    candidates.push(store(true));
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i] && isUsable(candidates[i])) return candidates[i];
    }
    return state.enabled[0] || KEYS[0];
  }

  /* Off-tab hero photos ship with data-src/data-srcset so the first paint only
     downloads the active panel; promote to real sources when a panel activates
     (and for everything left, once the page has loaded and idled). */
  function promoteImages(scope) {
    $all('img[data-src], img[data-srcset]', scope).forEach(function (img) {
      var ss = img.getAttribute('data-srcset');
      var s = img.getAttribute('data-src');
      if (ss) { img.setAttribute('srcset', ss); img.removeAttribute('data-srcset'); }
      if (s) { img.setAttribute('src', s); img.removeAttribute('data-src'); }
    });
  }

  /* ---- apply the current tab to the whole document ------------------------ */
  function apply(root) {
    var tab = state.tab;
    var label = state.labels[tab] || FALLBACK_LABELS[tab] || tab;
    $all('[data-est-tabs]', root).forEach(function (el) {
      var on = tokens(el).indexOf(tab) > -1;
      if (on) el.setAttribute('data-est-on', ''); else el.removeAttribute('data-est-on');
      if (on) promoteImages(el);
    });
    $all('[data-est-tab-button]', root).forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-est-tab-button') === tab ? 'true' : 'false');
    });
    $all('[data-est-tabgroup]', root).forEach(function (group) {
      var btns = $all('[data-est-tab-button]', group);
      var active = btns.filter(function (b) { return b.getAttribute('aria-pressed') === 'true'; })[0] || btns[0];
      btns.forEach(function (b) { b.setAttribute('tabindex', b === active ? '0' : '-1'); });
      if (active) nudgeIntoRow(group, active);
      updateFade(group);
    });
    $all('[data-est-text="tab_label"]', root).forEach(function (el) {
      /* NBSP-joined so a two-word label ("brand activations") never splits
         across a heading break and orphans half the italic phrase. */
      el.textContent = label.toLowerCase().replace(/\s+/g, '\u00a0');
    });
    if (state.summary) applySummary(state.summary, root);
  }

  /* Horizontal-only scroll so a pressed pill is visible in a scrolling row
     (never scrollIntoView — that would yank the page to the hero). Snap rows
     declare scroll-padding-inline equal to their edge fades, so the pill is
     parked exactly on that snap edge: full ink, and nothing left for the
     browser's proximity snap to "correct" after the scroll (centring first
     used to re-snap the pill under the fade). Rows without a numeric snap
     padding get the pill centred. Positions come from rects, not offsetLeft,
     so the row's offsetParent never matters. */
  function nudgeIntoRow(group, btn) {
    if (group.scrollWidth <= group.clientWidth + 1) return;
    var cs = getComputedStyle(group);
    var pad = parseFloat(cs.scrollPaddingInlineStart || cs.scrollPaddingLeft);
    var snaps = (cs.scrollSnapType || 'none') !== 'none' && !isNaN(pad);
    var rel = btn.getBoundingClientRect().left - group.getBoundingClientRect().left + group.scrollLeft;
    var left = snaps ? rel - pad : rel - (group.clientWidth - btn.offsetWidth) / 2;
    left = Math.round(Math.max(0, Math.min(left, group.scrollWidth - group.clientWidth)));
    if (Math.abs(group.scrollLeft - left) < 1) return;
    try { group.scrollTo({ left: left, behavior: reduced() ? 'auto' : 'smooth' }); }
    catch (e) { group.scrollLeft = left; }
  }
  function updateFade(group) {
    if (group.scrollWidth <= group.clientWidth + 1) { group.removeAttribute('data-est-fade'); return; }
    var atStart = group.scrollLeft <= 2;
    var atEnd = group.scrollLeft + group.clientWidth >= group.scrollWidth - 2;
    group.setAttribute('data-est-fade', atStart ? 'right' : (atEnd ? 'left' : 'both'));
  }

  function setTab(key, opts) {
    opts = opts || {};
    if (!isUsable(key)) return false;
    var changed = key !== state.tab;
    state.tab = key;
    apply();
    if (changed || opts.source === 'init') {
      store(false, key);
      /* Never write #<tab> into a clean URL on first paint — only once the
         visitor actually interacts (or the URL already carried a hash). */
      if (opts.source !== 'init' || window.location.hash) writeHash(key);
    }
    if (changed) emit('bb:est:tab', { tab: key, label: state.labels[key], source: opts.source || 'api' });
    if (opts.scroll) scrollTo(opts.scroll);
    return true;
  }
  /* Keep ?search; only write #<tab> when the hash is empty or already a tab key
     so a foreign anchor (#estimate after a form post) is never clobbered. */
  function writeHash(key) {
    var cur = (window.location.hash || '').replace(/^#/, '');
    if (cur && KEYS.indexOf(cur) === -1) return;
    if (cur === key) return;
    try { window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + '#' + key); } catch (e) { /* ignore */ }
  }
  function scrollTo(target) {
    var el = target === true ? heroGroup() : (typeof target === 'string' ? document.querySelector(target) : target);
    if (!el || !el.scrollIntoView) return;
    el.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
  }
  function heroGroup() {
    return $all('[data-est-tabgroup]').filter(function (g) { return !g.closest('[data-est-minibar]'); })[0] || null;
  }

  /* ---- summary relay from the builder ------------------------------------- */
  /* With nothing selected the range text is cleared and the label+range cluster
     hides — the bar must never read "Your estimate $0". The CTA pill stays
     visible either way (it anchors the bar's right edge and keeps a conversion
     job before the first tap); its label swaps between data-est-cta-empty
     ("Build an estimate") and data-est-cta-active ("Email my estimate"). */
  function applySummary(d, root) {
    var has = !!d && d.count > 0;
    $all('[data-est-summary-range]', root).forEach(function (el) { el.textContent = has ? (d.rangeText || '') : ''; });
    $all('[data-est-summary-tally]', root).forEach(function (el) { el.hidden = !has; });
    $all('[data-est-summary-cta]', root).forEach(function (el) {
      el.hidden = false;
      var swap = el.getAttribute(has ? 'data-est-cta-active' : 'data-est-cta-empty');
      if (!swap) return;
      var label = el.querySelector('[data-est-cta-label]') || el;
      if (label.textContent !== swap) label.textContent = swap;
    });
  }
  document.addEventListener('bb:est:summary', function (e) {
    state.summary = e.detail || {};
    applySummary(state.summary);
    /* A summary change can toggle html.est-dock-on, which collapses the
       mini-bar tally and resizes the pill row — recheck the edge fades so a
       stale mask never shades a row that no longer scrolls. */
    $all('[data-est-tabgroup]').forEach(updateFade);
  });

  /* ---- events: click + keyboard (delegated, so editor reloads need no rewire) */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-est-tab-button]');
    if (!btn) return;
    e.preventDefault();
    setTab(btn.getAttribute('data-est-tab-button'), { source: 'button' });
  });
  document.addEventListener('keydown', function (e) {
    var btn = e.target.closest && e.target.closest('[data-est-tab-button]');
    if (!btn) return;
    var group = btn.closest('[data-est-tabgroup]');
    if (!group) return;
    var btns = $all('[data-est-tab-button]', group);
    var i = btns.indexOf(btn);
    var next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % btns.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + btns.length) % btns.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = btns.length - 1;
    if (next < 0) return;
    e.preventDefault();
    btns.forEach(function (b, j) { b.setAttribute('tabindex', j === next ? '0' : '-1'); });
    btns[next].focus();
    nudgeIntoRow(group, btns[next]);
  });
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (t && t.nodeType === 1 && t.hasAttribute && t.hasAttribute('data-est-tabgroup')) updateFade(t);
  }, true);
  window.addEventListener('resize', function () { $all('[data-est-tabgroup]').forEach(updateFade); });

  /* ---- row geometry watcher ----------------------------------------------- */
  /* The mini-bar's pill row changes width without a window resize or summary
     event: the builder's IntersectionObserver toggles html.est-dock-on, which
     collapses the bar's tally+CTA cluster and widens the row. Watching the
     rows themselves catches every cause (dock toggle, resize, font load), so
     the active pill is re-centered and the edge fade never goes stale. */
  var rowRO = null;
  var rowPending = [];
  var rowRAF = 0;
  function rowRecheck(group) {
    var active = $all('[data-est-tab-button]', group).filter(function (b) { return b.getAttribute('aria-pressed') === 'true'; })[0];
    if (active) nudgeIntoRow(group, active);
    updateFade(group);
  }
  function wireRowWatch() {
    if (!('ResizeObserver' in window)) return;
    if (rowRO) rowRO.disconnect();
    rowRO = new ResizeObserver(function (entries) {
      entries.forEach(function (en) {
        var g = en.target.hasAttribute('data-est-tabgroup') ? en.target : en.target.closest('[data-est-tabgroup]');
        if (g && rowPending.indexOf(g) === -1) rowPending.push(g);
      });
      if (rowRAF) return;
      rowRAF = window.requestAnimationFrame(function () {
        rowRAF = 0;
        var q = rowPending;
        rowPending = [];
        q.forEach(rowRecheck);
      });
    });
    $all('[data-est-tabgroup]').forEach(function (g) {
      rowRO.observe(g);
      /* The pills resize too (web-font swap, the phone-only short labels),
         which moves every snap point without touching the row's box — a
         landing on #corporate used to park the pill half off-screen from
         pre-font metrics. */
      $all('[data-est-tab-button]', g).forEach(function (b) { rowRO.observe(b); });
    });
  }

  /* ---- dock paint guard ---------------------------------------------------- */
  /* The builder parks its fixed dock at translateY(102%) when off; full-page
     rasterizers still paint that off-canvas strip (an empty-range ghost bar in
     the middle of captures). Inline visibility keeps the off state out of
     paint without touching the builder section's transform transition; the
     hide waits out the slide so the exit still animates. */
  function wireDockGuard() {
    $all('[data-est-dock]').forEach(function (dock) {
      if (dock.__bbDockGuard) return;
      dock.__bbDockGuard = true;
      if (!dock.hasAttribute('data-est-dock-on')) dock.style.visibility = 'hidden';
      if (!('MutationObserver' in window)) { dock.style.visibility = ''; return; }
      new MutationObserver(function () {
        window.clearTimeout(dock.__bbDockT);
        if (dock.hasAttribute('data-est-dock-on')) {
          dock.style.visibility = '';
        } else {
          dock.__bbDockT = window.setTimeout(function () {
            if (!dock.hasAttribute('data-est-dock-on')) dock.style.visibility = 'hidden';
          }, reduced() ? 0 : 400);
        }
      }).observe(dock, { attributes: true, attributeFilter: ['data-est-dock-on'] });
    });
  }

  /* ---- Klaviyo guard ------------------------------------------------------- */
  /* This page is a pricing decision surface. About 8s after load Klaviyo's
     signup modal mounts a full-viewport wrapper (z-index 90000) AND stamps
     body.klaviyo-prevent-body-scrolling, whose stylesheet rule is
     overflow:hidden — a dead builder and a page that no longer scrolls by
     wheel, key or touch, for every visitor. The source fix is Klaviyo form
     targeting excluding /pages/estimate; until that lands this is the
     backstop. Rules learned across rounds:
       - never click Klaviyo's close button (r4: a click mid-animation strands
         an invisible full-viewport overlay that outlives the modal);
       - never leave the body class in place (r5: the node was removed but the
         class stayed, so the lock outlived the modal it served);
       - lift the class and the inline styles unconditionally — on this page
         the modal is display:none'd by the shared stylesheet, so the lock
         never has anything to serve.
     It neutralises (display:none, pointer-events:none — never removes: pulling
     nodes out from under Klaviyo's React tree throws insertBefore errors on
     its next render) the modal mount, any Klaviyo node painted fixed over the
     whole viewport (the invisible-overlay case) and the teaser (it shares the
     phone bottom strip with the builder dock), then re-runs on every
     body/html class, style or childList mutation so a re-asserted lock never
     survives a frame. Inline Klaviyo embeds are untouched. */
  var KL_LOCK = 'klaviyo-prevent-body-scrolling';
  var KL_ROOT = 'div[class*="kl-private-reset-css"]';
  var klGuarded = false;
  var klDebounce = null;
  /* Klaviyo mounts each form as its own body-level tree whose every div
     carries the kl-private-reset-css class: climb to the outermost one so the
     whole mount goes dark, not just the layer that matched. */
  function klMount(el) {
    var n = el;
    var top = el;
    while (n && n.nodeType === 1 && n !== document.body) {
      if (n.matches && n.matches(KL_ROOT)) top = n;
      n = n.parentElement;
    }
    return top;
  }
  function coversViewport(el) {
    var r = el.getBoundingClientRect();
    return r.width >= window.innerWidth * 0.9 && r.height >= window.innerHeight * 0.9;
  }
  function unlockKlaviyo() {
    var body = document.body;
    if (!body) return;
    var locked = body.classList.contains(KL_LOCK) || html.classList.contains(KL_LOCK);
    body.classList.remove(KL_LOCK);
    html.classList.remove(KL_LOCK);
    var doomed = [];
    function doom(el) { var m = klMount(el); if (doomed.indexOf(m) === -1) doomed.push(m); }
    $all('[data-kl-scroll-locking-modal]').forEach(doom);
    $all(KL_ROOT + ' [aria-modal="true"]').forEach(doom);
    $all('[class*="kl-teaser"]').forEach(doom);
    $all(KL_ROOT).forEach(function (n) {
      if (getComputedStyle(n).position === 'fixed' && coversViewport(n)) doom(n);
    });
    var hit = false;
    doomed.forEach(function (n) {
      if (n.hasAttribute('data-est-kl-off') && n.style.getPropertyValue('display') === 'none') return;
      n.style.setProperty('display', 'none', 'important');
      n.style.setProperty('pointer-events', 'none', 'important');
      n.setAttribute('data-est-kl-off', '');
      hit = true;
    });
    /* Only unwind inline locks when Klaviyo's own class or node was present —
       the theme's drawers scroll-lock legitimately and are never touched. */
    if (!hit && !locked) return;
    if (body.style.overflow === 'hidden') body.style.overflow = '';
    if (body.style.position === 'fixed') body.style.position = '';
    if (html.style.overflow === 'hidden') html.style.overflow = '';
  }
  function wireKlaviyoGuard() {
    if (klGuarded || !('MutationObserver' in window)) return;
    klGuarded = true;
    /* childList catches the mount; class + style catch a lock asserted
       without a remount (the r5 failure). Everything funnels through one
       60ms debounce, so the subtree scope stays cheap. */
    var mo = new MutationObserver(function () {
      if (klDebounce) return;
      klDebounce = window.setTimeout(function () { klDebounce = null; unlockKlaviyo(); }, 60);
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    mo.observe(html, { attributes: true, attributeFilter: ['class', 'style'] });
    unlockKlaviyo();
    /* Belt and braces for whatever a third-party script does next: a bounded
       heartbeat through the window Klaviyo is known to fire in (it mounts at
       ~8s; the loader starts it up to 4s after load or on first intent). */
    var beats = 0;
    var hb = window.setInterval(function () {
      unlockKlaviyo();
      if (++beats >= 30) window.clearInterval(hb);
    }, 2000);
  }

  /* ---- mini-bar: on once the hero tab switcher is fully above the viewport - */
  /* Off again while the builder's dock owns the bottom edge on ≤999px
     (html.est-dock-on): tab switching is one flick up, and the bar was 65px of
     fixed chrome over the item rows on a 664px phone. The builder mirrors
     .is-on into --est-minibar-h, so its sticky jump row rises to the header
     with the bar and comes back down when the bar returns. */
  var miniIO = null;
  var miniMO = null;
  var miniBar = null;
  var miniAbove = false;
  var dockMQ = window.matchMedia('(max-width: 999px)');
  function paintMinibar() {
    if (!miniBar) return;
    var on = miniAbove && !(dockMQ.matches && html.classList.contains('est-dock-on'));
    if (miniBar.classList.contains('is-on') !== on) miniBar.classList.toggle('is-on', on);
  }
  function wireMinibar() {
    var bar = document.querySelector('[data-est-minibar]');
    var group = heroGroup();
    if (miniIO) { miniIO.disconnect(); miniIO = null; }
    miniBar = bar;
    if (!bar || !group || !('IntersectionObserver' in window)) return;
    var offset = parseFloat(getComputedStyle(html).getPropertyValue('--header-height')) || 0;
    miniIO = new IntersectionObserver(function (entries) {
      var en = entries[entries.length - 1];
      miniAbove = !en.isIntersecting && en.boundingClientRect.bottom - offset < 0;
      paintMinibar();
    }, { threshold: 0, rootMargin: (-offset) + 'px 0px 0px 0px' });
    miniIO.observe(group);
    if (!miniMO && 'MutationObserver' in window) {
      miniMO = new MutationObserver(paintMinibar);
      miniMO.observe(html, { attributes: true, attributeFilter: ['class'] });
    }
  }
  if (dockMQ.addEventListener) dockMQ.addEventListener('change', paintMinibar);

  /* ---- reveal: opt-in, IO + sweep + failsafe (corp pattern) --------------- */
  var revIO = null;
  var pending = [];
  function revealEl(el) {
    if (el.classList.contains('in')) return;
    el.classList.add('in');
    if (revIO) revIO.unobserve(el);
    var i = pending.indexOf(el);
    if (i > -1) pending.splice(i, 1);
  }
  function revealAll() { $all('.est-rev').forEach(function (el) { el.classList.add('in'); }); pending = []; }
  function sweep() {
    if (!pending.length) return;
    var line = window.innerHeight * 0.92;
    pending.slice().forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < line && (r.width || r.height)) revealEl(el);
    });
  }
  var sweepQueued = false;
  function queueSweep() {
    if (sweepQueued) return;
    sweepQueued = true;
    window.setTimeout(function () { sweepQueued = false; sweep(); }, 50);
  }
  function wireReveal(root) {
    var canAnimate = 'IntersectionObserver' in window && !reduced();
    if (!canAnimate) { html.classList.remove('est-anim'); revealAll(); return; }
    html.classList.add('est-anim');
    if (!revIO) {
      revIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting || en.boundingClientRect.top < 0) revealEl(en.target);
        });
      }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
      window.addEventListener('scroll', queueSweep, { passive: true });
      window.addEventListener('resize', queueSweep);
      document.addEventListener('visibilitychange', queueSweep);
    }
    $all('.est-rev', root).forEach(function (el) {
      if (el.classList.contains('in')) return;
      if (pending.indexOf(el) === -1) pending.push(el);
      revIO.observe(el);
    });
    window.setTimeout(function () { pending.slice().forEach(revealEl); }, 1500);
  }
  if (reduceMQ.addEventListener) reduceMQ.addEventListener('change', function () { if (reduced()) { html.classList.remove('est-anim'); revealAll(); } });

  /* ---- theme editor ------------------------------------------------------- */
  function refresh(root) {
    collect();
    if (!isUsable(state.tab)) state.tab = state.enabled[0] || KEYS[0];
    apply();
    wireMinibar();
    wireRowWatch();
    wireDockGuard();
    wireReveal(root);
  }
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-est-tabs]') : null;
      if (!el) return;
      /* Intersect the tab tokens of EVERY [data-est-tabs] ancestor (a Corporate
         item inside a Weddings-only group is visible on no tab at all), then
         filter to enabled keys before switching. */
      var allowed = null;
      var node = el;
      while (node) {
        var toks = tokens(node).filter(function (t) { return KEYS.indexOf(t) > -1; });
        if (toks.length) {
          allowed = allowed === null ? toks : allowed.filter(function (t) { return toks.indexOf(t) > -1; });
        }
        node = node.parentElement && node.parentElement.closest ? node.parentElement.closest('[data-est-tabs]') : null;
      }
      if (allowed === null) return;
      allowed = allowed.filter(isUsable);
      if (allowed.length && allowed.indexOf(state.tab) === -1) setTab(allowed[0], { source: 'editor' });
    });
    document.addEventListener('shopify:section:load', function (e) { refresh(e.target); });
    document.addEventListener('shopify:section:select', function (e) { refresh(e.target); });
  }

  /* ---- public API --------------------------------------------------------- */
  window.bbEstimate = {
    get tab() { return state.tab; },
    get labels() { return state.labels; },
    get enabled() { return state.enabled.slice(); },
    get ready() { return state.ready; },
    setTab: setTab,
    refresh: refresh
  };

  /* ---- init --------------------------------------------------------------- */
  function init() {
    collect();
    state.tab = resolveInitial();
    setTab(state.tab, { source: 'init' });
    wireMinibar();
    wireRowWatch();
    wireDockGuard();
    wireKlaviyoGuard();
    wireReveal();
    state.ready = true;
    emit('bb:est:ready', { tab: state.tab, label: state.labels[state.tab] });
  }
  window.addEventListener('load', function () {
    window.setTimeout(function () { promoteImages(document); }, 1500);
  });
  window.addEventListener('pageshow', function (e) {
    if (!state.ready) return;
    if (e.persisted) { collect(); if (!isUsable(state.tab)) state.tab = state.enabled[0] || KEYS[0]; }
    apply();
    wireMinibar();
    unlockKlaviyo();
    queueSweep();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
